import React, { Dispatch, SetStateAction, useState } from "react";
import { Avatar, Input, Typography, Flex } from "antd";
import { toast } from "react-toastify";
import { CloseOutlined } from "@ant-design/icons";
import { BsMegaphone } from "react-icons/bs";
import { useAppDispatch } from "../../../../redux/utils/Dispatcher";
import { setCurrentChat } from "../../../../redux/slices/chatSlice";
import { formatSingleChat } from "../../../../utils";
import { useChatSidebarContext } from "../../../../context/ChatSidebarScreenContext";
import apiInstance from "../../../../services/apiService";
import ENDPOINTS from "../../../../services/endpoint";
import ChatSidebarHeader from "../../../../components/common/ChatSidebarHeader";
import GradientButton from "../../../../components/common/UI/GradientButton";
import "./index.scss";

const MegaphoneIcon = BsMegaphone as unknown as React.FC;
const { Text } = Typography;

interface Contact {
  _id: string;
  fullName: string;
  phoneNumber: string;
  emailAddress: string;
  iseFone: boolean;
}

interface CreateBroadcastProps {
  selectedMembers: Contact[] | null | [];
  handleBackClick: () => void;
  setSelectedMembers: Dispatch<SetStateAction<any>>;
}

const CreateBroadcast: React.FC<CreateBroadcastProps> = ({
  selectedMembers,
  handleBackClick,
  setSelectedMembers,
}) => {
  const dispatch = useAppDispatch();
  const { setCurrentScreen } = useChatSidebarContext();
  const [groupName, setGroupName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const handleRemoveMember = (member: Contact) => {
    if (loading) return;
    setSelectedMembers((prev: any) => {
      return prev.filter((c: Contact) => c !== member);
    });
  };

  const handleCreateBroadcast = async () => {
    if (!groupName) {
      toast.error("Broadcast name is required");
      return;
    }

    if (selectedMembers && selectedMembers?.length < 2) {
      toast.error("Please select at least 2 recipients");
      return;
    }

    const URL = ENDPOINTS.CREATE_BROADCAST;
    setLoading(true);

    const broadCastMembers = selectedMembers?.map(
      (member) => member.phoneNumber
    );

    try {
      const response = await apiInstance.post(URL, {
        name: groupName,
        broadCastMembers,
      });

      if (response.status === 200) {
        setCurrentScreen("chats");
        dispatch(setCurrentChat(formatSingleChat(response.data.conversation)));
      }
    } catch (err: any) {
      toast.error(err.message || "Error creating group. Please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-broadcast-screen">
      <ChatSidebarHeader
        title="New Broadcast"
        handleBackClick={handleBackClick}
      />

      <div className="name-container">
        <div className="broadcast-icon">
          <MegaphoneIcon />
        </div>

        <Input
          value={groupName}
          disabled={loading}
          onChange={(e) => setGroupName(e.target.value)}
          className="custom-input group-name-input"
          placeholder="Broadcast name"
        />
      </div>

      <div className="banner">{selectedMembers?.length} Members</div>

      <div className="selected-members-container">
        <Flex align="center" gap={8} wrap>
          {selectedMembers?.map((member: Contact) => (
            <div className="member">
              <CloseOutlined
                onClick={() => handleRemoveMember(member)}
                className="cancel-icon"
              />
              <Avatar className="member-avatar" size={50}>
                {member?.fullName.slice(0, 1)}
              </Avatar>
              <Text className="member-name">{member?.fullName}</Text>
              <Text className="member-number">{member?.phoneNumber}</Text>
            </div>
          ))}
        </Flex>
        <GradientButton
          loading={loading}
          onClick={handleCreateBroadcast}
          className="create-btn"
        >
          Create
        </GradientButton>
      </div>
    </div>
  );
};

export default CreateBroadcast;
